import { DateTime } from 'luxon';
import { createSelector } from 'reselect';
import { DEFAULT_DATE_FORMAT } from '../constants/dateFormatConstants';
import {
  MONTH,
} from '../constants/dateConstants';
import { getPropKey, getSecondSelectorParameter, getStateKey } from './commonSelectors';
import { isdateWithinCheckInLimit } from '../utils/calendarDataUtils';

export const getStartDate = getStateKey('startDate');
export const getEndDate = getStateKey('endDate');
export const getFocusedField = getStateKey('focusedField');
export const getAllPossibleDatesAsStrings = getStateKey('allPossibleDatesAsStrings');
export const getAllPossibleDatesByIndex = getStateKey('allPossibleDatesByIndex');
export const getCalendarData = getStateKey('calendarData');

export const getCalendarDataForSpecificDate = createSelector(
  getSecondSelectorParameter,
  getCalendarData,
  (stringDate, calendarData = {}) => calendarData[stringDate] || {}
);

export const getMinShownDateString = createSelector(
  getAllPossibleDatesByIndex,
  (allPossibleDatesByIndex = {}) => allPossibleDatesByIndex['0']
);
export const getMinShownDateLuxon = createSelector(
  getMinShownDateString,
  (minShownDateString) => {
    if (!minShownDateString) {
      return null;
    }
    return DateTime.fromFormat(minShownDateString, DEFAULT_DATE_FORMAT);
  }
);

export const getMaxShownDateString = createSelector(
  getAllPossibleDatesByIndex,
  (allPossibleDatesByIndex = {}) => {
    const totalDays = Object.keys(allPossibleDatesByIndex).length;
    if (totalDays > 0) {
      return allPossibleDatesByIndex[`${totalDays - 1}`];
    }
    return null;
  }
);
export const getMaxShownDateLuxon = createSelector(
  getMaxShownDateString,
  (maxShownDateString) => {
    if (!maxShownDateString) {
      return null;
    }
    return DateTime.fromFormat(maxShownDateString, DEFAULT_DATE_FORMAT);
  }
);

export const getMaxDaysAdvanceFromToday = createSelector(
  getAllPossibleDatesByIndex,
  (allPossibleDatesByIndex = {}) => Object.keys(allPossibleDatesByIndex).length
);

export const getIsPrevBtnShown = createSelector(
  getMinShownDateLuxon,
  getSecondSelectorParameter,
  (minShownDate, shownDate) => minShownDate?.startOf(MONTH) < shownDate?.startOf(MONTH)
);

export const getIsNextBtnShown = createSelector(
  getMaxShownDateLuxon,
  getSecondSelectorParameter,
  (maxShownDate, shownDate) => !maxShownDate || (
    maxShownDate?.startOf(MONTH) > shownDate?.startOf(MONTH)
  )
);

export const getIsDateDisabled = createSelector(
  getCalendarDataForSpecificDate,
  ({
    isEnabled,
  } = {}) => !isEnabled
);

export const getIsDateAvailable = createSelector(
  getCalendarDataForSpecificDate,
  ({ isAvailable } = {}) => !!isAvailable
);

export const getIsTooltipToBeShown = createSelector(
  getCalendarData,
  getStartDate,
  getSecondSelectorParameter,
  (calendarData, startDate, stringDate) => {
    if (startDate) {
      const { minNights = null } = calendarData[startDate] || {};
      const { isAvailable = null } = calendarData[stringDate] || {};
      const calendarDateValue = DateTime.fromFormat(stringDate, DEFAULT_DATE_FORMAT).valueOf();
      const luxonStartDate = DateTime.fromFormat(startDate, DEFAULT_DATE_FORMAT);
      const checkinLimitValue = luxonStartDate.plus({ days: minNights }).valueOf();
      const luxonStartDateValue = luxonStartDate.valueOf();
      if (isdateWithinCheckInLimit({
        isAvailable, calendarDateValue, checkinLimitValue, luxonStartDateValue,
      })) {
        return true;
      }
    }
    return false;
  }
);

export const getNextAvailableStringDate = createSelector(
  getCalendarData,
  getAllPossibleDatesAsStrings,
  getAllPossibleDatesByIndex,
  getSecondSelectorParameter,
  // eslint-disable-next-line max-params -- this is how selector functions work
  (calendarData, allPossibleDatesAsStrings, allPossibleDatesByIndex, stringDate) => {
    const totalDays = Object.keys(allPossibleDatesAsStrings).length;
    const dateIndex = allPossibleDatesAsStrings[stringDate];
    if (!Number.isInteger(dateIndex)) {
      return null;
    }
    let daysAfter = 0;
    while (dateIndex + daysAfter < totalDays) {
      const nextStringDate = allPossibleDatesByIndex[dateIndex + daysAfter];
      const {
        isAvailable,
      } = calendarData[nextStringDate];
      if (isAvailable) {
        return nextStringDate;
      }
      daysAfter += 1;
    }
    return null;
  }
);

export const getPreviousAvailableStringDate = createSelector(
  getCalendarData,
  getAllPossibleDatesAsStrings,
  getAllPossibleDatesByIndex,
  getSecondSelectorParameter,
  // eslint-disable-next-line max-params -- this is how selector functions work
  (calendarData, allPossibleDatesAsStrings, allPossibleDatesByIndex, stringDate) => {
    const dateIndex = allPossibleDatesAsStrings[stringDate];
    if (!Number.isInteger(dateIndex)) {
      return null;
    }
    let daysBefore = 0;
    while (dateIndex - daysBefore >= 0) {
      const nextStringDate = allPossibleDatesByIndex[dateIndex - daysBefore];
      const {
        isAvailable,
      } = calendarData[nextStringDate];
      if (isAvailable) {
        return nextStringDate;
      }
      daysBefore += 1;
    }
    return null;
  }
);

export const getIsDateRange = getStateKey('isDateRange');

export const getIsDoneBtnClicked = getStateKey('isDoneBtnClicked');

export const getIsDoneBtnDisabled = createSelector(
  getStartDate,
  getEndDate,
  getIsDateRange,
  (startDate, endDate, isDateRange) => (isDateRange ? !startDate || !endDate : !startDate)
);

export const getIsAbleToResetDates = createSelector(
  getIsDateRange,
  getStartDate,
  getEndDate,
  (isDateRange, startDate, endDate) => (isDateRange ? startDate || endDate : !!startDate)
);

/* START OF PROP SELECTORS
The following are prop selectors. Due to the unique setup of context in this repo, these work for
components but not inside of reducers. If you need to update a value inside a reducer, you
will need to add it to the useReducer state. */
export const getIsStartDateWarning = getPropKey('isStartDateWarning');
export const getIsEndDateWarning = getPropKey('isEndDateWarning');
export const getIsMinStayVisible = getPropKey('isMinStayVisible');

export const getFooterMinNightsStayLabelValue = createSelector(
  getIsMinStayVisible,
  getIsDateRange,
  getStartDate,
  getCalendarData,
  // eslint-disable-next-line max-params -- this is how selector functions work
  (isMinStayVisible, isDateRange, startDate, calendarData) => {
    if (!isDateRange || !isMinStayVisible) {
      return null;
    }
    const { minNights = null } = calendarData[startDate] || {};
    return minNights;
  }
);
// END OF PROP SELECTORS
